import React, { useState } from 'react';
import {
    BarChart2,
    TrendingUp,
    Download,
    Filter,
    AlertCircle,
    Users,
    Clock,
    FileText
} from 'lucide-react';

const ReportDemo = () => {
    // 模擬報表數據
    const [reportData] = useState({
        attendanceMetrics: {
            totalEmployees: 250,
            averageAttendanceRate: 96.5,
            lateCases: 12,
            absentCases: 5
        },
        performanceAnalysis: {
            departmentPerformance: [
                {
                    department: '銷售部門',
                    performanceScore: 88.5,
                    kpiAchievement: 92,
                    trend: 'up'
                },
                {
                    department: '技術研發部',
                    performanceScore: 85.3,
                    kpiAchievement: 88,
                    trend: 'stable'
                },
                {
                    department: '客服部門',
                    performanceScore: 82.7,
                    kpiAchievement: 85,
                    trend: 'down'
                }
            ],
            monthlyProductivity: [
                { month: '1月', value: 85 },
                { month: '2月', value: 88 },
                { month: '3月', value: 92 }
            ]
        },
        salaryInsights: {
            averageSalary: 52000,
            salaryDistribution: [
                { range: '30,000以下', percentage: 15 },
                { range: '30,001-50,000', percentage: 45 },
                { range: '50,001-70,000', percentage: 30 },
                { range: '70,001以上', percentage: 10 }
            ]
        }
    });

    // 篩選和導出功能
    const [filters, setFilters] = useState({
        department: '全部',
        dateRange: '本季度'
    });

    // 獲取趨勢圖示
    const getTrendIcon = (trend) => {
        switch (trend) {
            case 'up':
                return <TrendingUp className="text-green-500" />;
            case 'down':
                return <TrendingUp className="text-red-500 rotate-180" />;
            default:
                return <Clock className="text-gray-500" />;
        }
    };

    return (
        <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8">
                {/* 左側：篩選與報表控制 */}
                <div>
                    <h2 className="text-2xl font-bold mb-4 flex items-center">
                        <BarChart2 className="mr-2 text-primary-600" />
                        報表與數據分析
                    </h2>

                    <div className="bg-gray-50 p-6 rounded-lg mb-6">
                        <h3 className="font-semibold mb-4 flex items-center">
                            <Filter className="mr-2 text-primary-600" />
                            報表篩選條件
                        </h3>

                        <div className="space-y-4">
                            <div>
                                <label className="block mb-2 text-sm font-medium">部門選擇</label>
                                <select
                                    value={filters.department}
                                    onChange={(e) => setFilters(prev => ({
                                        ...prev,
                                        department: e.target.value
                                    }))}
                                    className="w-full p-2 border rounded-lg"
                                >
                                    <option>全部</option>
                                    <option>銷售部門</option>
                                    <option>技術研發部</option>
                                    <option>客服部門</option>
                                </select>
                            </div>

                            <div>
                                <label className="block mb-2 text-sm font-medium">日期範圍</label>
                                <select
                                    value={filters.dateRange}
                                    onChange={(e) => setFilters(prev => ({
                                        ...prev,
                                        dateRange: e.target.value
                                    }))}
                                    className="w-full p-2 border rounded-lg"
                                >
                                    <option>本季度</option>
                                    <option>本月</option>
                                    <option>本年度</option>
                                </select>
                            </div>
                        </div>

                        <div className="mt-6 flex space-x-4">
                            <button className="flex-1 bg-primary-600 text-white p-3 rounded-lg hover:bg-primary-700 flex items-center justify-center">
                                <FileText className="mr-2" />
                                生成報表
                            </button>
                            <button className="flex-1 border border-primary-600 text-primary-600 p-3 rounded-lg hover:bg-primary-50 flex items-center justify-center">
                                <Download className="mr-2" />
                                匯出Excel
                            </button>
                        </div>
                    </div>
                </div>

                {/* 右側：關鍵指標總覽 */}
                <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center">
                        <Users className="mr-2 text-primary-600" />
                        關鍵績效指標總覽
                    </h3>

                    <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm text-gray-600">出勤率</span>
                                <Clock className="text-primary-600" />
                            </div>
                            <div className="text-2xl font-bold text-primary-600">
                                {reportData.attendanceMetrics.averageAttendanceRate}%
                            </div>
                            <div className="text-sm text-gray-600">
                                總員工數：{reportData.attendanceMetrics.totalEmployees}人
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm text-gray-600">異常情況</span>
                                <AlertCircle className="text-red-600" />
                            </div>
                            <div className="flex justify-between">
                                <div>
                                    <span className="text-xl font-bold text-red-600">
                                        {reportData.attendanceMetrics.lateCases}
                                    </span>
                                    <div className="text-sm text-gray-600">遲到次數</div>
                                </div>
                                <div>
                                    <span className="text-xl font-bold text-red-600">
                                        {reportData.attendanceMetrics.absentCases}
                                    </span>
                                    <div className="text-sm text-gray-600">缺勤次數</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md">
                        <div className="p-4 border-b">
                            <h4 className="font-semibold">部門績效分析</h4>
                        </div>
                        {reportData.performanceAnalysis.departmentPerformance.map((dept) => (
                            <div
                                key={dept.department}
                                className="p-4 border-b flex items-center justify-between"
                            >
                                <div>
                                    <div className="font-medium">{dept.department}</div>
                                    <div className="text-sm text-gray-600">
                                        績效得分：{dept.performanceScore}
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <span className="mr-2 text-sm text-gray-600">
                                        KPI達成率：{dept.kpiAchievement}%
                                    </span>
                                    {getTrendIcon(dept.trend)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 底部操作區域 */}
            <div className="mt-8 flex justify-between items-center bg-white p-6 rounded-lg shadow-md">
                <div>
                    <h4 className="font-semibold">深入數據分析</h4>
                    <p className="text-sm text-gray-600">
                        提供客製化的數據分析報告，協助企業做出數據驅動的決策
                    </p>
                </div>
                <div className="flex space-x-4">
                    <button className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors">
                        詳細報告
                    </button>
                    <button className="border border-primary-600 text-primary-600 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors">
                        數據儀表板
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReportDemo;